// Administration › Coding stats: how much code each release (each "merge and deploy") changed, from the
// git history. scripts/coding-stats.mjs writes /coding-stats.json on every deploy; this page reads it.
// A diverging column chart (lines added above the line, removed below, one column per release) and the
// full, sortable table, which is also the chart's accessible view.
import { useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState, ErrorState, Fact, FactEnd, FactSentence, FilterBar, LoadingState, PageHeader, SearchInput, Segmented } from '../../components/ui.js';
import { SortMark } from '../../components/SortMark.js';
import { useFormatDate } from '../../lib/format.js';
import { fold, hasText } from '../../lib/search.js';
import { sortRows, useTableSort } from '../../lib/tableSort.js';
import { useTabState } from '../../lib/tabState.js';
import { useCodingStats, type Release } from '../../features/codingStats.js';
import { AdminBack } from './AdminHub.js';

const n = (v: number) => v.toLocaleString('en-GB');
type Range = '30' | '100' | 'all';
type SortKey = 'version' | 'date' | 'commits' | 'files' | 'insertions' | 'deletions' | 'net';

export function AdminCodingStats() {
  const { data, isLoading, error, refetch } = useCodingStats();
  const fmt = useFormatDate();
  const [range, setRange] = useTabState<Range>('codingstats.range', '30');
  const [q, setQ] = useState('');
  const { sort, dirOf, th } = useTableSort<SortKey>('coding-stats');

  const releases = data?.releases ?? []; // newest first
  const totals = useMemo(() => releases.reduce(
    (a, r) => ({ ins: a.ins + r.insertions, del: a.del + r.deletions, commits: a.commits + r.commits }),
    { ins: 0, del: 0, commits: 0 },
  ), [releases]);
  const latest = releases[0];
  const first = releases[releases.length - 1];

  // the chart reads left to right, oldest to newest
  const charted = useMemo(() => {
    const chrono = [...releases].reverse();
    return range === 'all' ? chrono : chrono.slice(-Number(range));
  }, [releases, range]);

  const rows = useMemo(() => {
    const query = fold(q.trim());
    const hit = releases.filter((r) => hasText(query, r.version, r.title, r.commit));
    return sortRows(hit, sort, (r, key) => {
      if (key === 'net') return r.insertions - r.deletions;
      if (key === 'version') return r.version.slice(1); // "2.0.17.24": the dictionary order reads its numbers
      return r[key];
    });
  }, [releases, q, sort]);

  return (
    <>
      <AdminBack />
      <PageHeader
        title="Coding stats"
        meta={data ? <>Lines changed per release, from the git history · updated {fmt(data.generatedAt.slice(0, 10))}</> : undefined}
      />
      {isLoading && <LoadingState label="Loading the coding stats…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && !releases.length && <EmptyState title="No releases yet" hint="Releases appear here after the first merge and deploy." />}

      {data && latest && first && (
        <>
          <FactSentence>
            <Fact>{n(releases.length)} releases</Fact> since {fmt(first.date)} have added <Fact>{n(totals.ins)} lines</Fact> and
            removed <Fact>{n(totals.del)}</Fact> across <Fact>{n(totals.commits)} commits</Fact><FactEnd>.</FactEnd>
          </FactSentence>
          <FactSentence sub>
            The latest, <b>{latest.version}</b>{latest.title ? <> ({latest.title})</> : null}, changed {n(latest.files)} files:{' '}
            {n(latest.insertions)} lines added and {n(latest.deletions)} removed in {latest.commits} commit{latest.commits === 1 ? '' : 's'}.
          </FactSentence>

          <section className="card cs-chart-card" aria-labelledby="cs-chart-h">
            <div className="cs-chart-hd">
              <h2 id="cs-chart-h">Lines changed per release</h2>
              <ul className="cs-legend" aria-label="Key">
                <li><i className="cs-sw add" aria-hidden="true" />Added</li>
                <li><i className="cs-sw del" aria-hidden="true" />Removed</li>
              </ul>
              <Segmented
                value={range}
                onChange={setRange}
                ariaLabel="Releases shown in the chart"
                options={[{ value: '30', label: 'Last 30' }, { value: '100', label: 'Last 100' }, { value: 'all', label: `All ${releases.length}` }]}
              />
            </div>
            <ReleaseChart releases={charted} fmt={fmt} />
          </section>

          <FilterBar activeCount={q.trim() ? 1 : 0} onClear={() => setQ('')} search={<SearchInput value={q} onChange={setQ} placeholder="Search version or summary" width={260} />}>
            <span className="muted">{rows.length === releases.length ? `${releases.length} releases` : `${rows.length} of ${releases.length}`}</span>
          </FilterBar>
          <div className="tbl-wrap cs-tbl-wrap">
            <table className="tbl cs-tbl">
              <thead>
                <tr>
                  <th {...th('version')}>Version<SortMark dir={dirOf('version')} /></th>
                  <th {...th('date')}>Date<SortMark dir={dirOf('date')} /></th>
                  <th>Summary</th>
                  <th {...th('commits', 'num')}>Commits<SortMark dir={dirOf('commits')} /></th>
                  <th {...th('files', 'num')}>Files<SortMark dir={dirOf('files')} /></th>
                  <th {...th('insertions', 'num')}>Added<SortMark dir={dirOf('insertions')} /></th>
                  <th {...th('deletions', 'num')}>Removed<SortMark dir={dirOf('deletions')} /></th>
                  <th {...th('net', 'num')}>Net<SortMark dir={dirOf('net')} /></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const net = r.insertions - r.deletions;
                  return (
                    <tr key={r.version + r.commit}>
                      <td className="cs-ver">{r.version}{r.commit ? <small>{r.commit}</small> : <small>this release</small>}</td>
                      <td className="cs-date">{fmt(r.date)}</td>
                      <td className="cs-title">{r.title || <span className="muted">–</span>}</td>
                      <td className="num">{n(r.commits)}</td>
                      <td className="num">{n(r.files)}</td>
                      <td className="num">+{n(r.insertions)}</td>
                      <td className="num">−{n(r.deletions)}</td>
                      <td className="num">{net >= 0 ? '+' : '−'}{n(Math.abs(net))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="muted cs-note">
            Git&rsquo;s own line counts between one release marker and the one before it. Lockfiles ({data.excluded.join(', ')}) are left out,
            since an install rewrites thousands of their lines. A file that only moved counts its changed lines, not the whole file.
          </p>
        </>
      )}
    </>
  );
}

/** the chart's width follows its box */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

const quantile = (vals: number[], p: number) => {
  if (!vals.length) return 0;
  const s = [...vals].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(p * (s.length - 1)))]!;
};
const niceStep = (v: number) => {
  const p = 10 ** Math.floor(Math.log10(Math.max(1, v)));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
};

/** diverging columns: one per release, lines added rising from the baseline, removed falling from it.
 *  A few releases dwarf the rest (a vendored library, say), so the scale stops at the 95th percentile
 *  when the largest value is far above it; a column past the scale is cut with a notch and its value
 *  written at the cut. Hovering a release shows its figures. */
function ReleaseChart({ releases, fmt }: { releases: Release[]; fmt: (d: string) => string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const H = 250, padL = 52, padR = 8, padT = 22, padB = 24;

  const geo = useMemo(() => {
    const ins = releases.map((r) => r.insertions), del = releases.map((r) => r.deletions);
    const max = Math.max(1, ...ins, ...del);
    const q95 = quantile([...ins, ...del], 0.95);
    const cap = max > q95 * 2 && q95 > 0 ? q95 * 1.25 : max;
    const step = niceStep(cap / 3);
    const upMax = Math.min(cap, Math.max(1, ...ins)), downMax = Math.min(cap, Math.max(0, ...del));
    const inner = H - padT - padB;
    const total = upMax + downMax || 1;
    const base = padT + inner * (upMax / total); // the zero line, placed by how far each side reaches
    const k = inner / total; // px per line
    return { cap, step, base, k, upMax, downMax };
  }, [releases]);

  if (!releases.length) return null;
  const innerW = Math.max(0, width - padL - padR);
  const colW = innerW / releases.length;
  const barW = Math.max(1, Math.min(24, colW - Math.min(2, colW * 0.3)));
  const x = (i: number) => padL + i * colW + (colW - barW) / 2;
  const ticks: number[] = [];
  for (let t = geo.step; t <= geo.upMax; t += geo.step) ticks.push(t);
  for (let t = geo.step; t <= geo.downMax; t += geo.step) ticks.push(-t);
  const r = Math.min(4, barW / 2);
  // a column with a rounded end away from the baseline (square at the baseline)
  const col = (xx: number, y0: number, len: number, up: boolean) => {
    if (len <= 0) return '';
    const rr = Math.min(r, len);
    return up
      ? `M${xx},${y0}V${y0 - len + rr}Q${xx},${y0 - len} ${xx + rr},${y0 - len}H${xx + barW - rr}Q${xx + barW},${y0 - len} ${xx + barW},${y0 - len + rr}V${y0}Z`
      : `M${xx},${y0}V${y0 + len - rr}Q${xx},${y0 + len} ${xx + rr},${y0 + len}H${xx + barW - rr}Q${xx + barW},${y0 + len} ${xx + barW},${y0 + len - rr}V${y0}Z`;
  };
  const hv = hover != null ? releases[hover] : null;
  // a version label is ~70px wide: label every k-th column so neighbours never touch, the newest always
  const labelEvery = Math.max(1, Math.ceil(84 / Math.max(1, colW)));
  // the value of a cut column is written at the cut only when the column is wide enough to hold it
  const cutLabels = colW >= 46;
  const summary = `${releases.length} releases from ${releases[0]!.version} to ${releases[releases.length - 1]!.version}. The table below lists every figure.`;

  return (
    <div ref={ref} className="cs-chart" onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={H} role="img" aria-label={summary}>
          {ticks.map((t) => (
            <g key={t} className="cs-grid">
              <line x1={padL} x2={width - padR} y1={geo.base - t * geo.k} y2={geo.base - t * geo.k} />
              <text x={padL - 6} y={geo.base - t * geo.k + 4} textAnchor="end">{t > 0 ? '+' : '−'}{n(Math.abs(t))}</text>
            </g>
          ))}
          {hover != null && <rect className="cs-hoverband" x={padL + hover * colW} y={padT - 6} width={colW} height={H - padT - padB + 12} />}
          {releases.map((rel, i) => {
            const up = Math.min(rel.insertions, geo.cap) * geo.k, dn = Math.min(rel.deletions, geo.cap) * geo.k;
            const cutUp = rel.insertions > geo.cap, cutDn = rel.deletions > geo.cap;
            return (
              <g key={rel.version + rel.commit}>
                <path className="cs-bar add" d={col(x(i), geo.base - 1, up, true)} />
                <path className="cs-bar del" d={col(x(i), geo.base + 1, dn, false)} />
                {cutUp && <><line className="cs-cut" x1={x(i) - 1} x2={x(i) + barW + 1} y1={geo.base - up + 5} y2={geo.base - up + 2} />{cutLabels && <text className="cs-cutlbl" x={x(i) + barW / 2} y={geo.base - up - 4} textAnchor="middle">+{n(rel.insertions)}</text>}</>}
                {cutDn && <><line className="cs-cut" x1={x(i) - 1} x2={x(i) + barW + 1} y1={geo.base + dn - 5} y2={geo.base + dn - 2} />{cutLabels && <text className="cs-cutlbl" x={x(i) + barW / 2} y={geo.base + dn + 12} textAnchor="middle">−{n(rel.deletions)}</text>}</>}
              </g>
            );
          })}
          <line className="cs-zero" x1={padL} x2={width - padR} y1={geo.base} y2={geo.base} />
          {releases.map((rel, i) => (i === releases.length - 1 || (i % labelEvery === 0 && releases.length - 1 - i >= labelEvery)) ? (
            <text key={`l${i}`} className="cs-xlbl" x={padL + i * colW + colW / 2} y={H - 6} textAnchor={i === releases.length - 1 ? 'end' : i === 0 ? 'start' : 'middle'}>{rel.version}</text>
          ) : null)}
          {/* hit targets: the whole column height, wider than the bar */}
          {releases.map((rel, i) => (
            <rect key={`h${i}`} className="cs-hit" x={padL + i * colW} y={padT - 6} width={colW} height={H - padT - padB + 12} onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
      )}
      {hv && hover != null && (
        <div className="cs-tip" style={{ left: Math.min(Math.max(8, padL + hover * colW + colW / 2 - 120), Math.max(8, width - 248)) }} role="status">
          <b>{hv.version}</b> <span className="muted">{fmt(hv.date)}</span>
          {hv.title ? <div className="cs-tip-t">{hv.title}</div> : null}
          <div className="cs-tip-n">
            <span><i className="cs-sw add" aria-hidden="true" />{n(hv.insertions)} added</span>
            <span><i className="cs-sw del" aria-hidden="true" />{n(hv.deletions)} removed</span>
          </div>
          <div className="muted">{n(hv.files)} files · {hv.commits} commit{hv.commits === 1 ? '' : 's'}{hv.insertions > geo.cap || hv.deletions > geo.cap ? ' · taller than the scale, cut short' : ''}</div>
        </div>
      )}
    </div>
  );
}
